import moment from 'moment';
import React, { useState } from 'react';
import {
  useAttractionsQuery,
  useRemoveAttractionMutation,
  useSaveAttractionMutation,
} from '@/queries/attractions';
import { withServerDateFields } from '@/lib/server-date';
import { useFestivalWorkspaceStore } from '@/stores/festival-workspace-store';
import { FestivalHeader, Loading } from '../controls';
import Portal from '../modal/Portal';
import { SectionNavbar } from '../section-navbar';
import { AssignedSponsors } from '../sponsors';
import FilterableTable from '../table';
import { AttractionForm } from './';

export function Attractions() {
  const festival = useFestivalWorkspaceStore((s) => s.festival);
  const festivalId = festival?.festivalId;

  const attractionsQ = useAttractionsQuery(festivalId);
  const saveMutation = useSaveAttractionMutation(festivalId);
  const removeMutation = useRemoveAttractionMutation(festivalId);

  const attractions = attractionsQ.data ?? [];
  const loading =
    festivalId == null || attractionsQ.isPending || attractionsQ.isFetching;

  const [showModal, setShowModal] = useState(false);
  const [selectedAttraction, setSelectedAttraction] = useState({});

  const buildColumnDefinitions = () => {
    var columnDefs = {};
    columnDefs.attractionName = {
      header: 'Attraction Name',
      filterable: true,
      sortable: true,
      showViewLink: false,
      handleClick: true,
    };
    columnDefs.startTime = {
      header: 'Start Time',
      filterable: false,
      sortable: false,
    };
    columnDefs.endTime = {
      header: 'End Time',
      filterable: false,
      sortable: false,
    };
    return columnDefs;
  };

  const openModal = (attraction) => {
    const next =
      attractions.find((a) => a.attractionId === attraction.attractionId) || {};
    setSelectedAttraction(next);
    setShowModal(true);
  };

  const handleDateChange = (dates) => {
    const updatedAttraction = { ...selectedAttraction, ...dates[0] };
    setSelectedAttraction(updatedAttraction);
  };

  const handleLocationChange = (location) => {
    const updatedLocation = {
      ...selectedAttraction.location,
      ...location,
      radius: 50,
    };
    setSelectedAttraction((prev) => ({
      ...prev,
      location: updatedLocation,
    }));
  };

  const handleLocationNameChange = (locationName) => {
    const updatedLocation = {
      ...selectedAttraction.location,
      locationName,
    };
    setSelectedAttraction((prev) => ({
      ...prev,
      location: updatedLocation,
    }));
  };

  const onFileUpload = (fileInfo) => {
    const { filename, url } = fileInfo;
    setSelectedAttraction((prev) => ({
      ...prev,
      photoURL: url,
      attractionPhoto: filename,
    }));
  };

  const onFileDelete = () => {
    setSelectedAttraction((prev) => ({
      ...prev,
      photoURL: null,
      attractionPhoto: null,
    }));
  };

  const handleSave = async (attraction) => {
    const updatedAttraction = withServerDateFields({
      ...selectedAttraction,
      ...attraction,
    });
    try {
      await saveMutation.mutateAsync(updatedAttraction);
      setShowModal(false);
    } catch {
      /* mutation toast */
    }
  };

  const handleRemove = async () => {
    try {
      await removeMutation.mutateAsync(selectedAttraction.attractionId);
      setShowModal(false);
      setSelectedAttraction({});
    } catch {
      /* mutation toast */
    }
  };

  const tableData = attractions.map((attraction) => ({
    ...attraction,
    startTime: attraction.startTime
      ? moment(attraction.startTime).format('YYYY-MM-DD h:mm a')
      : '-',
    endTime: attraction.endTime
      ? moment(attraction.endTime).format('YYYY-MM-DD h:mm a')
      : '-',
  }));

  const attractionForm = (
    <AttractionForm
      attraction={selectedAttraction}
      handleSave={handleSave}
      onDateChange={handleDateChange}
      handleRemove={handleRemove}
      onLocationChange={handleLocationChange}
      onLocationNameChange={handleLocationNameChange}
      onFileUpload={onFileUpload}
      onFileDelete={onFileDelete}
    />
  );

  const sponsorsComponent = (
    <AssignedSponsors
      objectId={selectedAttraction.attractionId}
      objectType="attraction"
    />
  );

  const sections = [
    {
      title: 'Information',
      component: attractionForm,
    },
    {
      title: 'Sponsors',
      component: sponsorsComponent,
    },
  ];

  return (
    <>
      <FestivalHeader
        title="Attractions"
        showExit={true}
        showAdd={true}
        addTitle="Add Attraction"
        addAction={() => {
          setSelectedAttraction({});
          setShowModal(true);
        }}
      />
      {loading ? (
        <Loading />
      ) : (
        <FilterableTable
          data={tableData}
          tableTitle=""
          columns={buildColumnDefinitions()}
          includeFilter={false}
          editHandler={openModal}
          toggleModal={openModal}
        />
      )}

      <Portal
        header={
          selectedAttraction
            ? selectedAttraction.attractionName || 'Add New Attraction'
            : 'Add New Attraction'
        }
        open={showModal}
        onClose={() => setShowModal(false)}
      >
        <SectionNavbar navItems={sections} />
      </Portal>
    </>
  );
}
